import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';

import { useQueryClient } from '@tanstack/react-query';
import Alert from 'components/Alert';
import type IProduct from 'interfaces/IProduct';
import { useDesactiveProductOffer } from 'v2/hooks/useDesactiveProductOffer';
import type { ProductOffer } from 'v2/interfaces/product';

import { LinksModal } from '../LinksModal';

type ActionsMenuProps = {
  hasUnsavedChanges: boolean;
  product: IProduct;
  offer: ProductOffer;
  openConfirmModal: (cancelModalCallback?: undefined | (() => void)) => void;
  children: React.ReactNode;
};

function ActionsMenu({ children, product, offer, hasUnsavedChanges, openConfirmModal }: ActionsMenuProps) {
  const [isLinksModalOpen, setIsLinksModalOpen] = React.useState(false);

  const queryClient = useQueryClient();
  const history = useHistory();
  const desactiveOfferMutation = useDesactiveProductOffer({
    onSuccess: () => {
      toast.success('Oferta excluída com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['products', product.id, 'offers'] });
    },
    onError: () => {
      toast.error('Ocorreu um erro ao excluir a oferta! Tente novamente.');
    }
  });

  function handleLinksClick() {
    setIsLinksModalOpen(true);
  }

  function navigateToEditOffer() {
    history.push(`/producer/product/${product.id}/offers/${offer?.id}/edit`, {
      product,
      offer
    });
  }

  function handleEditClick() {
    if (hasUnsavedChanges) {
      openConfirmModal(navigateToEditOffer);
      return;
    }

    navigateToEditOffer();
  }

  async function handleDeleteClick() {
    const confirm = await Alert.confirm({
      title: 'Confirmação de exclusão',
      message: 'Tem certeza que deseja excluir esta oferta?',
      confirmation: true,
      buttonOk: 'Excluir',
      buttonCancel: 'Cancelar'
    });

    if (confirm) {
      toast.info('Excluindo oferta...');
      await desactiveOfferMutation.mutateAsync({
        productId: product.id,
        offerId: offer?.id
      });
    }
  }

  const items: MenuProps['items'] = [
    {
      key: 'edit-offer',
      label: 'Editar',
      onClick: handleEditClick
    },
    {
      key: 'links-offer',
      label: 'Links desta oferta',
      onClick: handleLinksClick
    },
    ...(!offer?.is_default
      ? [
          {
            key: 'delete-offer',
            label: 'Excluir',
            onClick: handleDeleteClick
          }
        ]
      : [])
  ];

  return (
    <>
      <Dropdown menu={{ items }}>{children}</Dropdown>
      <LinksModal isOpen={isLinksModalOpen} closeModal={() => setIsLinksModalOpen(false)} offer={offer} />
    </>
  );
}

export { ActionsMenu };
